import React from "react";
import PropTypes from "prop-types";
import InfoIcon from "../InfoIcon";
import {
  typography,
  borders,
  textColors,
  surfaces,
  interactive,
} from "../../../design-system/tokens";

// ---------------------------------------------------------------------------
// Entrance Animation Field
// ---------------------------------------------------------------------------
//
// A visual picker for the hero's ``animated_entrance_choice`` field. Users
// see a chip per available animation with a directional arrow glyph that
// *previews the motion on hover* — the arrow fades + translates to mimic
// where the content will enter from. Only the hovered chip animates so we
// don't overwhelm the user with six simultaneous loops.
//
// Mounted by ``EditField.js`` when metadata reports
// ``appearance === "special_entrance_animation"``. The available choices
// come from Django (``ANIMATED_ENTRANCE_CHOICES``) via ``metadata.choices``.
// ---------------------------------------------------------------------------

// Per-animation glyph descriptor. The ``directionLabel`` is what we announce
// to screen readers; the ``Glyph`` is an inline SVG fragment that the chip
// renders inside an ``<svg>`` viewBox. Keeping the geometry here (not
// scattered across components) makes it easy to swap a glyph without
// touching the animation wiring.
const ENTRANCE_GLYPHS = {
  // Upward arrow — content fades in from below, moving up.
  "fade-up": {
    directionLabel: "Enters from below",
    Glyph: () => (
      <g>
        <line x1="12" y1="5" x2="12" y2="19" />
        <polyline points="6 11 12 5 18 11" />
      </g>
    ),
  },
  // Downward arrow — content fades in from above, moving down.
  "fade-down": {
    directionLabel: "Enters from above",
    Glyph: () => (
      <g>
        <line x1="12" y1="5" x2="12" y2="19" />
        <polyline points="6 13 12 19 18 13" />
      </g>
    ),
  },
  // Rightward arrow — content enters from the left, travelling right.
  "slide-right": {
    directionLabel: "Enters from the left",
    Glyph: () => (
      <g>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="13 6 19 12 13 18" />
      </g>
    ),
  },
  // Leftward arrow — content enters from the right, travelling left.
  "slide-left": {
    directionLabel: "Enters from the right",
    Glyph: () => (
      <g>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="11 6 5 12 11 18" />
      </g>
    ),
  },
  // Center scale — dot ringed by a circle that grows into place.
  "scale-in": {
    directionLabel: "Grows into place",
    Glyph: () => (
      <g>
        <circle cx="12" cy="12" r="7" />
        <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
      </g>
    ),
  },
  // Card-sized rectangle rising into place — a heavier fade-up flavour.
  "card-slide-up": {
    directionLabel: "Card slides up",
    Glyph: () => (
      <g>
        <rect x="6" y="7" width="12" height="9" rx="1.5" />
        <line x1="9" y1="19" x2="15" y2="19" />
      </g>
    ),
  },
};

// The hover-preview keyframes per animation. Each keyframe mirrors the real
// entrance (same direction/intensity, just slowed so the eye can catch it)
// and loops infinitely by starting from the "offscreen" state, resting
// briefly at the target, then snapping back on the next iteration. We
// ship them as a raw string so a single inline ``<style>`` tag covers
// all chips; the selector is scoped to ``.entrance-chip:hover`` so only
// the chip actually being hovered animates.
const ENTRANCE_KEYFRAMES = `
  @keyframes entrance-preview-fade-up {
    0%   { opacity: 0.25; transform: translateY(10px); }
    55%  { opacity: 1;    transform: translateY(0); }
    100% { opacity: 1;    transform: translateY(0); }
  }
  @keyframes entrance-preview-fade-down {
    0%   { opacity: 0.25; transform: translateY(-10px); }
    55%  { opacity: 1;    transform: translateY(0); }
    100% { opacity: 1;    transform: translateY(0); }
  }
  @keyframes entrance-preview-slide-right {
    0%   { opacity: 0.25; transform: translateX(-12px); }
    55%  { opacity: 1;    transform: translateX(0); }
    100% { opacity: 1;    transform: translateX(0); }
  }
  @keyframes entrance-preview-slide-left {
    0%   { opacity: 0.25; transform: translateX(12px); }
    55%  { opacity: 1;    transform: translateX(0); }
    100% { opacity: 1;    transform: translateX(0); }
  }
  @keyframes entrance-preview-scale-in {
    0%   { opacity: 0.35; transform: scale(0.6); }
    55%  { opacity: 1;    transform: scale(1); }
    100% { opacity: 1;    transform: scale(1); }
  }
  @keyframes entrance-preview-card-slide-up {
    0%   { opacity: 0.25; transform: translateY(14px); }
    55%  { opacity: 1;    transform: translateY(0); }
    100% { opacity: 1;    transform: translateY(0); }
  }

  .entrance-chip .entrance-chip-icon {
    transform-origin: center;
    transition: transform 200ms ease-out;
  }

  .entrance-chip:hover .entrance-chip-icon[data-anim="fade-up"] {
    animation: entrance-preview-fade-up 1.4s ease-out infinite;
  }
  .entrance-chip:hover .entrance-chip-icon[data-anim="fade-down"] {
    animation: entrance-preview-fade-down 1.4s ease-out infinite;
  }
  .entrance-chip:hover .entrance-chip-icon[data-anim="slide-right"] {
    animation: entrance-preview-slide-right 1.4s ease-out infinite;
  }
  .entrance-chip:hover .entrance-chip-icon[data-anim="slide-left"] {
    animation: entrance-preview-slide-left 1.4s ease-out infinite;
  }
  .entrance-chip:hover .entrance-chip-icon[data-anim="scale-in"] {
    animation: entrance-preview-scale-in 1.4s ease-out infinite;
  }
  .entrance-chip:hover .entrance-chip-icon[data-anim="card-slide-up"] {
    animation: entrance-preview-card-slide-up 1.4s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .entrance-chip:hover .entrance-chip-icon {
      animation: none !important;
    }
  }
`;

function EntranceChip({ value, label, selected, onSelect }) {
  const glyph = ENTRANCE_GLYPHS[value];

  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={selected}
      aria-label={
        glyph?.directionLabel ? `${label} — ${glyph.directionLabel}` : label
      }
      title={
        glyph?.directionLabel
          ? `${label} — ${glyph.directionLabel}. Hover to preview.`
          : label
      }
      data-testid={`entrance-animation-chip-${value}`}
      className={`entrance-chip border rounded-lg px-3 py-3 flex flex-col items-center justify-center gap-1.5 transition-all w-full ${
        selected
          ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
          : `${borders.default} ${surfaces.tertiary} ${textColors.primary} ${surfaces.hover}`
      }`}
    >
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="entrance-chip-icon"
        data-anim={value}
        aria-hidden="true"
      >
        {glyph ? <glyph.Glyph /> : null}
      </svg>
      <span
        className={`${typography.caption} font-medium text-center leading-tight ${
          selected ? "text-white/90" : textColors.primary
        }`}
      >
        {label}
      </span>
    </button>
  );
}

EntranceChip.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default function EntranceAnimationField({
  label,
  help,
  value,
  onChange,
  choices,
  infoText,
}) {
  // ``choices`` comes from backend metadata (``ANIMATED_ENTRANCE_CHOICES``).
  // Fall back to the known glyph keys only when nothing is passed so tests
  // / preview contexts can render without a mocked metadata tree.
  const normalized =
    Array.isArray(choices) && choices.length > 0
      ? choices.map((c) => ({ value: c.value, label: c.label }))
      : Object.keys(ENTRANCE_GLYPHS).map((k) => ({ value: k, label: k }));

  return (
    <div className="field-wrapper">
      <label
        className={`block mb-2 ${typography.label} ${textColors.secondary}`}
      >
        {label}
        {infoText ? (
          <span className="ml-2 align-middle">
            <InfoIcon text={infoText} />
          </span>
        ) : null}
      </label>

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))" }}
      >
        {normalized.map((choice) => (
          <EntranceChip
            key={choice.value}
            value={choice.value}
            label={choice.label}
            selected={value === choice.value}
            onSelect={onChange}
          />
        ))}
      </div>

      {help ? (
        <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
          {help}
        </div>
      ) : null}

      {/* Scoped inline style tag — avoids plumbing a new global CSS file
          just for chip-hover previews. Kept at the end so the DOM reads
          naturally top-to-bottom. */}
      <style>{ENTRANCE_KEYFRAMES}</style>
    </div>
  );
}

EntranceAnimationField.propTypes = {
  label: PropTypes.string,
  help: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  choices: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  infoText: PropTypes.string,
};
